<script>
  import { fade } from "svelte/transition";
  import { isEnglish } from "../stores/cookies.js";

  let name = "";
  let email = "";
  let phone = "";
  let message = "";

  let isSubmitting = false;
  let showSuccess = false;
  let errorMessage = "";

  // Your PHP endpoint
  const PHP_ENDPOINT = "https://www.vredenburgvet.co.za/bat/MailSender.php";

  async function handleSubmit(e) {
    e.preventDefault();

    if (isSubmitting) return;
    isSubmitting = true;
    showSuccess = false;
    errorMessage = "";

    try {
      const response = await fetch(PHP_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim(),
        }),
      });

      const result = await response.json();

      if (result.success) {
        showSuccess = true;
        setTimeout(() => {
          showSuccess = false;
        }, 5000); // show success for 5 seconds

        // Reset form
        name = "";
        email = "";
        phone = "";
        message = "";

        // Refocus first field
        document.getElementById("name")?.focus();
      } else {
        errorMessage =
          result.message || "Could not send message. Please try again.";
      }
    } catch (err) {
      console.error("Submission error:", err);
      errorMessage = $isEnglish
        ? "Network error – please check your connection and try again."
        : "Netwerkfout – kontroleer jou verbinding en probeer weer.";
    } finally {
      isSubmitting = false;
    }
  }

  function clearForm() {
    name = "";
    email = "";
    phone = "";
    message = "";
    errorMessage = "";
    showSuccess = false;
  }
</script>

<form
  on:submit|preventDefault={handleSubmit}
  class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 bg-blue-50 rounded-xl shadow-md transition-all duration-200 relative group block"
  transition:fade={{ duration: 300 }}
>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-6">
    <!-- Name -->
    <div>
      <label for="name" class="block text-lg text-gray-900 mb-1 font-bold">
        {$isEnglish ? "Name" : "Naam"}
      </label>
      <input
        id="name"
        type="text"
        bind:value={name}
        maxlength="400"
        required
        autocomplete="name"
        placeholder={$isEnglish ? "John Doe" : "Jan van der Merwe"}
        class="w-full px-4 py-2.5 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
        disabled={isSubmitting}
      />
    </div>

    <!-- Email -->
    <div>
      <label for="email" class="block text-lg text-gray-900 mb-1 font-bold">
        {$isEnglish ? "Email" : "E-pos"}
      </label>
      <input
        id="email"
        type="email"
        bind:value={email}
        maxlength="400"
        required
        autocomplete="email"
        placeholder={$isEnglish ? "example@email.com" : "voorbeeld@epos.com"}
        class="w-full px-4 py-2.5 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
        disabled={isSubmitting}
      />
    </div>

    <!-- Phone -->
    <div>
      <label for="phone" class="block text-lg text-gray-900 mb-1 font-bold">
        {$isEnglish ? "Phone" : "Selfoon"}
      </label>
      <input
        id="phone"
        type="tel"
        bind:value={phone}
        maxlength="50"
        required
        autocomplete="tel"
        placeholder={$isEnglish ? "+27 82 123 4567" : "+27 82 123 4567"}
        class="w-full px-4 py-2.5 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition"
        disabled={isSubmitting}
      />
    </div>
  </div>

  <!-- Message -->
  <div class="mb-7">
    <label for="message" class="block text-lg text-gray-900 mb-1 font-bold">
      {$isEnglish ? "Message" : "Boodskap"}
    </label>
    <textarea
      id="message"
      bind:value={message}
      maxlength="1000"
      required
      rows="8"
      placeholder={$isEnglish
        ? "How can we help you today?"
        : "Hoe kan ons u vandag help?"}
      class="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition resize-y min-h-[140px]"
      disabled={isSubmitting}
    />
  </div>

  <!-- Feedback messages -->
  {#if showSuccess}
    <div
      transition:fade={{ duration: 400 }}
      class="mt-6 p-4 bg-green-100 border border-green-300 text-green-800 rounded-md text-center font-medium"
    >
      {$isEnglish
        ? "Message sent successfully!"
        : "Boodskap suksesvol gestuur!"}
    </div>
  {/if}

  {#if errorMessage}
    <div
      transition:fade={{ duration: 400 }}
      class="mt-6 p-4 bg-red-100 border border-red-300 text-red-800 rounded-md text-center"
    >
      {errorMessage}
    </div>
  {/if}

  <!-- Buttons -->
  <div class="flex flex-col sm:flex-row gap-4 justify-end mt-8">
    <button
      type="button"
      on:click={clearForm}
      disabled={isSubmitting}
      class="px-6 py-2.5 rounded-md bg-gray-200 text-gray-900 hover:bg-gray-300 active:bg-gray-400 transition text-lg font-bold disabled:opacity-60"
    >
      {$isEnglish ? "Clear" : "Maak skoon"}
    </button>

    <button
      type="submit"
      disabled={isSubmitting}
      class="px-6 py-2.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition text-lg shadow-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2"
    >
      {#if isSubmitting}
        <svg
          class="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      {/if}
      {isSubmitting
        ? $isEnglish
          ? "Sending..."
          : "Besig om te stuur..."
        : $isEnglish
          ? "Send"
          : "Stuur"}
    </button>
  </div>
</form>

<style>
  input:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
</style>
